import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, errorMessage, patch, put } from '../../api/client';
import type { BikeModel, Media } from '../../api/types';
import { ACCEPTED_IMAGES, uploadMany } from '../../api/upload';
import { IconTrash, IconUpload } from '../../components/icons';
import { DragHandle, SortableList } from '../../components/sortable';
import { Field, useFeedback } from '../../components/ui';
import { SaveBar } from './SaveBar';
import { useDraft, useUnloadGuard } from '../../hooks';
import { useT } from '../../i18n';
import { LIMITS, type TabProps } from './shared';

interface Item {
  uid: string;
  caption: string;
  media: Media;
}
interface Form {
  claim: [string, string, string];
  items: Item[];
}

const fromModel = (m: BikeModel): Form => ({
  claim: [m.claim[0] ?? '', m.claim[1] ?? '', m.claim[2] ?? ''],
  items: m.gallery.map((g) => ({ uid: g.id, caption: g.caption, media: g.media })),
});
const partA = (f: Form) => JSON.stringify(f.claim.map((l) => l.trim()));
const partB = (f: Form) => JSON.stringify(f.items.map((i) => [i.caption.trim(), i.media.id]));

export function GalleryTab({ model, onSaved, onDirty }: TabProps) {
  const { t } = useT();
  const initial = useMemo(() => fromModel(model), [model]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial, (a, b) => partA(a) === partA(b) && partB(a) === partB(b));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const { toast } = useFeedback();
  const dirtyA = partA(f) !== partA(initial);
  const dirtyB = partB(f) !== partB(initial);
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const add = async (files: File[]) => {
    const room = LIMITS.gallery - f.items.length;
    if (room <= 0) return toast(t('Máximo {max} fotos', { max: LIMITS.gallery }), 'error');
    const { media, failed } = await uploadMany(files.slice(0, room), (done, total) => setUploading(t('Subiendo {done} de {total}…', { done, total })));
    setUploading(null);
    setF((p) => ({ ...p, items: [...p.items, ...media.map((m) => ({ uid: crypto.randomUUID(), caption: '', media: m }))] }));
    for (const x of failed) toast(`${x.name}: ${x.error}`, 'error');
  };

  const save = async () => {
    const submitted = f;
    setBusy(true);
    setError(null);
    // Son dos pedidos: si el segundo falla, lo que guardó el primero no se pierde de vista.
    let saved: BikeModel | undefined;
    try {
      if (dirtyA) saved = (await patch<{ model: BikeModel }>(`/admin/models/${model.id}`, { claim: f.claim.map((l) => l.trim()) })).model;
      if (dirtyB) {
        saved = (
          await put<{ model: BikeModel }>(
            `/admin/models/${model.id}/gallery`,
            f.items.map((i) => ({ caption: i.caption.trim(), mediaId: i.media.id })),
          )
        ).model;
      }
      if (saved) onSaved(saved);
      markSaved(submitted);
      toast(t('Galería y claim guardados'));
    } catch (e) {
      if (saved) onSaved(saved);
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <section className="panel panel-pad stack">
        <div>
          <h2 className="section-title">{t('Claim')}</h2>
          <p className="sub" style={{ margin: 0 }}>
            {t('Tres líneas en mayúsculas sobre la galería, p. ej. «POTENCIA TU CAMINO, / DOMINA CADA CURVA, / SIENTE LA RUSH 150.»')}
          </p>
        </div>
        <div className="grid grid-3">
          {f.claim.map((line, i) => (
            <Field key={i} label={t('Línea {n}', { n: i + 1 })} count={{ value: line, max: LIMITS.claimLine }}>
              {(id) => (
                <input
                  id={id}
                  className="input"
                  value={line}
                  onChange={(e) => setF((p) => ({ ...p, claim: p.claim.map((l, j) => (j === i ? e.target.value.toUpperCase() : l)) as Form['claim'] }))}
                />
              )}
            </Field>
          ))}
        </div>
      </section>

      <section className="panel panel-pad stack">
        <div className="row between">
          <div>
            <h2 className="section-title">{t('Galería de detalles')}</h2>
            <p className="sub" style={{ margin: 0 }}>
              {t('Fotos verticales con un texto corto cada una. Arrastra para ordenarlas.')}
            </p>
          </div>
          <button className="btn" disabled={!!uploading} onClick={() => input.current?.click()}>
            <IconUpload /> {uploading ?? t('Agregar fotos')}
          </button>
        </div>
        <SortableList items={f.items} getId={(i) => i.uid} onReorder={(items) => setF((p) => ({ ...p, items }))} className="stack">
          {(item, i, handle) => (
            <div className="row" style={{ alignItems: 'stretch', gap: 12, padding: 8, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--panel)' }}>
              <div className="row">
                <DragHandle {...handle} />
              </div>
              <img src={item.media.url} alt="" style={{ width: 72, height: 96, objectFit: 'cover', borderRadius: 6, background: '#0e0e10' }} />
              <div style={{ flex: 1, display: 'grid', alignContent: 'center' }}>
                <Field label={t('Texto de la foto {n}', { n: i + 1 })} count={{ value: item.caption, max: LIMITS.caption }}>
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      value={item.caption}
                      placeholder={t('p. ej. Faro LED delantero para ver mejor en la oscuridad')}
                      onChange={(e) => setF((p) => ({ ...p, items: p.items.map((x) => (x.uid === item.uid ? { ...x, caption: e.target.value } : x)) }))}
                    />
                  )}
                </Field>
              </div>
              <div className="row">
                <button className="btn btn-ghost btn-icon" title={t('Quitar')} onClick={() => setF((p) => ({ ...p, items: p.items.filter((x) => x.uid !== item.uid) }))}>
                  <IconTrash />
                </button>
              </div>
            </div>
          )}
        </SortableList>
        {f.items.length === 0 && <div className="empty-state">{t('Sin fotos en la galería.')}</div>}
        <input
          ref={input}
          type="file"
          accept={ACCEPTED_IMAGES}
          multiple
          hidden
          onChange={(e) => {
            void add(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </section>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
